import React, { useEffect, useRef } from "react";
import I18n from "i18n";
import * as jQuery from "jquery";
import manualTagsInstance from "collections/manualTagsInstance";
import * as unorm from "unorm";
import AgentResultTagFormatter from "libraries/agentResultTagFormatter";
import select2CloseOnBlur from "libraries/select2CloseOnBlur";
import TaggingManager from "models/taggingManager";
import vent from "libraries/vent";

export interface ITag {
  id: string;
  text: string;
  existingTag: boolean;
}

interface Props {
  classNames?: string[];
  selectedAgentResults: Backbone.Collection<any>;
}

export default function DetailViewTagComponent({
  classNames = [],
  selectedAgentResults,
}: Props) {
  const tagInput = useRef<HTMLInputElement | null>(null);

  function getSelectedIds() {
    return selectedAgentResults.pluck("id");
  }

  function createTagging(tag: ITag) {
    new TaggingManager(selectedAgentResults).createTaggings(
      {
        name: tag.text.toString(),
        agent_result_ids: getSelectedIds(),
      },
      () => {}
    );
    taggingCallback();
  }

  function deleteTagging(tag: ITag) {
    new TaggingManager(selectedAgentResults).destroyTaggings({
      name: tag.text.toString(),
      agent_result_ids: getSelectedIds(),
    });
    taggingCallback();
  }

  function taggingCallback() {
    selectedAgentResults.each((model: any) => {
      vent.trigger("updatedByAgents", model.get("id"));
    });
  }

  // Keyboard shortcut logic ("t" to open select2 if input not focused)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const activeElem = document.activeElement;
      const isInputFocused =
        activeElem &&
        (activeElem.tagName === "INPUT" ||
          activeElem.tagName === "TEXTAREA" ||
          (activeElem as HTMLElement).isContentEditable);

      if (
        !isInputFocused &&
        (e.key === "t" || e.key === "T") &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        if (tagInput.current) {
          jQuery(tagInput.current).select2("open");
        }
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const markRegExp = () =>
    "\\u0300-\\u036F\\u0483-\\u0489\\u0591-\\u05BD\\u05BF\\u05C1\\u05C2\\u05C4\\u05C5\\u05C7";
  const numberRegExp = () =>
    "0-9\\u00B2\\u00B3\\u00B9\\u00BC-\\u00BE\\u0660-\\u0669\\u06F0-\\u06F9";
  const letterRegExp = () =>
    "A-Za-z\\u00AA\\u00B5\\u00BA\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u02C1";

  const matchTag = new RegExp(
    "^[" +
      letterRegExp() +
      markRegExp() +
      numberRegExp() +
      " \\-\\.\\_«»'´`§&\\$€\\?]+$"
  );

  useEffect(() => {
    const $input = jQuery(tagInput.current as HTMLInputElement);
    const tagFormatter = new AgentResultTagFormatter(selectedAgentResults);

    let createdNewTagText = "";

    const select2Options = {
      containerCssClass: "custom-select2",
      multiple: true,
      formatResult: (item: ITag) =>
        !item.id ? item.text : "<i class='icon-tag'></i> " + item.text,
      formatSelection: (item: ITag) =>
        !item.id ? item.text : "<i class='icon-tag'></i> " + item.id,
      formatLoadMore: I18n.t("webapp.general.loading_more"),
      createSearchChoice: (term: string) => {
        const normalized = unorm.nfc(term.trim());
        const newTagMessage = I18n.t(
          "webapp.agents.results.result_details.tags.new_tag"
        );
        if (matchTag.test(normalized)) {
          createdNewTagText = normalized + newTagMessage;
          return {
            id: normalized,
            text: createdNewTagText,
            existingTag: false,
          };
        }
      },
      formatNoMatches: I18n.t(
        "webapp.agents.results.result_details.tags.name_invalid"
      ),
      initSelection: (el: any, cb: any) => {
        cb(tagFormatter.initSelectionArray(el));
      },
      width: "100%",
      sortResults: (data: ITag[], _: any, query: { term: string }) => {
        const term = query?.term?.toLowerCase() || "";
        const starts = data.filter(
          (i) => i.existingTag && i.id.toLowerCase().startsWith(term)
        );
        const contains = data.filter(
          (i) =>
            i.existingTag &&
            term &&
            !i.id.toLowerCase().startsWith(term) &&
            i.id.toLowerCase().includes(term)
        );
        const newOnes = data.filter((i) => !i.existingTag);
        return [...starts, ...contains, ...newOnes];
      },
      data: manualTagsInstance.toSelect2().map((t: string) => ({
        id: t,
        text: t,
        existingTag: true,
      })),
      matcher(params: string, data: string) {
        return data.toLowerCase().includes(params.toLowerCase().trim());
      },
    };

    $input.select2(select2Options);
    select2CloseOnBlur($input);

    $input.on("change", (e: any) => {
      if (e.added) createTagging({ ...e.added, text: e.added.id });
      if (e.removed) deleteTagging(e.removed);
    });

    return () => {
      $input.select2("destroy");
      $input.off();
    };
  }, [selectedAgentResults]);

  const inputClassNames = ["js-select2-input", "mtl", ...classNames];
  const tagFormatter = new AgentResultTagFormatter(selectedAgentResults);
  const tags = tagFormatter.stringTags();

  return (
    <div className="row">
      <form>
        <input
          id="tags"
          ref={tagInput}
          type="text"
          className={inputClassNames.join(" ")}
          defaultValue={tags}
          data-placeholder={I18n.t(
            "webapp.agents.results.result_details.tags.placeholder"
          )}
        />
      </form>
    </div>
  );
}
